// ---------------------------------------------------------------------------
// saveDesignSystemToLibrary — POST to platform.cogo.xyz /api/design-systems
// to create a new user-owned preset. Replaces the v6.5 client-direct
// save-preset.ts (which is being kept for backwards-compat but should not
// be called from new code).
// ---------------------------------------------------------------------------

import { getStudioAccessToken } from "@/services/auth-provider";
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaderRecord,
} from "@/services/cogo-platform-config";

export interface SaveLibraryParams {
  name: string;
  description?: string;
  tokens: unknown;
  isPublic?: boolean;
}

export interface SaveLibraryResult {
  success: boolean;
  data?: { id: string; name: string; description?: string };
  error?: string;
}

async function getUserAccessToken(): Promise<string | null> {
  return getStudioAccessToken();
}

export async function saveDesignSystemToLibrary(
  params: SaveLibraryParams,
): Promise<SaveLibraryResult> {
  const jwt = await getUserAccessToken();
  if (!jwt) return { success: false, error: "Sign in required" };

  try {
    const res = await fetch(cogoPlatformApiUrl("/api/design-systems"), {
      method: "POST",
      headers: withCogoDevProxyHeaderRecord({
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
      }),
      body: JSON.stringify({
        name: params.name,
        description: params.description ?? "",
        tokens: params.tokens,
        is_public: params.isPublic ?? false,
      }),
    });

    const json = (await res.json().catch(() => ({}))) as {
      id?: string;
      name?: string;
      description?: string;
      error?: string;
    };

    if (!res.ok) {
      return { success: false, error: json.error ?? `HTTP ${res.status}` };
    }
    if (!json.id || !json.name) {
      return { success: false, error: "Malformed response (missing id/name)" };
    }
    // Notify all same-origin tabs (Starter runtime + Studio editor) to drop stale cache.
    try {
      const ch = new BroadcastChannel("cogo-preset-cache");
      ch.postMessage({ type: "invalidate", presetId: json.id });
      ch.close();
    } catch {
      // BroadcastChannel unavailable in some test envs — non-fatal
    }
    return {
      success: true,
      data: {
        id: json.id,
        name: json.name,
        description:
          typeof json.description === "string" ? json.description : undefined,
      },
    };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : "Network error",
    };
  }
}
